// src/panels/PanelHome.jsx
import React from 'react';
import { Box, Typography, Card, CardActionArea, Grid } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { COVER_OPTIONS } from '../data/constants';
import { DeadlineBanner, ProgressRing, ChildAvatar } from '../components/SharedComponents';
import coverImage from '../assets/cover_image.webp';

export default function PanelHome({ child, isPro, limits, onNavigate, progress, bookConfig,sections = [], }) {
  const cover = COVER_OPTIONS.find((c) => c.id === child?.coverStyle) || COVER_OPTIONS[0];

  const noteLimit = Number(limits.personalNotes) > 0 ? Number(limits.personalNotes) : 0;
  const noteCount = Number(progress.personalNotes) || 0;

  const routeKeyBySectionKey = {
  personal_photos: 'photos',
  personal_notes: 'personal_notes',
  memories: 'memories',
  school_photos: 'school_photos',
  cover: 'cover',
  child_details: 'child',
  shipping: 'order',
  payment: 'order',
};

const iconBySectionKey = {
  personal_photos: '📷',
  personal_notes: '💌',
  memories: '⭐',
  school_photos: '🏫',
  cover: '📚',
  child_details: '✏️',
  shipping: '📦',
  payment: '📦',
};

const unitBySectionKey = {
  personal_photos: 'added',
  personal_notes: 'added',
  memories: 'filled',
  school_photos: 'selected',
  cover: 'chosen',
  child_details: 'done',
  shipping: 'saved',
  payment: 'paid',
};

const liveProgressBySectionKey = {
  personal_photos: Number(progress?.photos) || 0,
  personal_notes: Number(progress?.personalNotes) || 0,
  memories: Number(progress?.memories) || 0,
  school_photos: Number(progress?.schoolPhotos) || 0,
};

const liveLimitBySectionKey = {
  personal_photos: Number(limits?.photos) || 0,
  personal_notes: Number(limits?.personalNotes) || 0,
  memories: Number(limits?.memories) || 0,
  school_photos: Number(limits?.schoolPhotos) || 0,
};

const steps = (sections || [])
  // Delivery + payment are one screen now. The legacy shipping section is
  // intentionally suppressed so progress/navigation does not show duplicates.
  .filter((section) => section.visible && section.show_in_progress && section.key !== 'shipping')
  .map((section) => {
    const hasLiveProgress = Object.prototype.hasOwnProperty.call(liveProgressBySectionKey, section.key);
    const liveDone = liveProgressBySectionKey[section.key];
    const sectionDone = Number(section.progress?.current || 0);
    const liveTotal = liveLimitBySectionKey[section.key];
    const sectionTotal = Number(section.progress?.target || 0);
    const done = hasLiveProgress ? liveDone : sectionDone;
    const total = hasLiveProgress && liveTotal > 0 ? liveTotal : sectionTotal;
    const complete = total > 0 ? done >= total : Boolean(section.progress?.complete);

    return {
      key: routeKeyBySectionKey[section.key] || section.route,
      label: section.key === 'payment' ? 'Order & Delivery' : section.label,
      icon: iconBySectionKey[section.key] || '•',
      done,
      total,
      complete,
      unit: unitBySectionKey[section.key] || 'done',
    };
  });

const completedCount = steps.filter((s) => s.complete).length;
const pct = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;
  // Contextual nudges
  const nudges = steps
  .filter((step) => !step.complete)
  .map((step) => {
    const remaining = Math.max((Number(step.total) || 0) - (Number(step.done) || 0), 0);

    return {
      key: step.key,
      icon: step.icon,
      text:
        step.key === 'photos'
          ? `Add ${remaining} more personal photos`
          : step.key === 'personal_notes'
            ? (step.done > 0 ? 'Add another personal note' : 'Add a personal note')
            : step.key === 'memories'
              ? `${remaining} memories left to fill`
              : `Complete ${step.label}`,
    };
  });


  return (
    <Box>
      <DeadlineBanner bookConfig={bookConfig} />

      {/* Hero */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 4.5 }}>
        {/* Book preview */}
        {/* Book preview */}
      <Box
        sx={{
          width: { xs: 90, sm: 110 },
          height: { xs: 90, sm: 110 },
          borderRadius: 0,
          flexShrink: 0,
          boxShadow: '4px 6px 20px rgba(0,0,0,0.08)',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'grey.100',
        }}
      >
        <Box
          component="img"
          src={coverImage}
          alt={`${child?.first_name}'s yearbook cover`}
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
          }}
        />
      </Box>

        <Box>
          <Typography variant="h1" sx={{ mb: 0.5 }}>
            {child?.first_name} {child?.last_name}'s Yearbook
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
            {child?.classroom} · Age {child?.age} · {cover.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <ProgressRing value={pct} />
            <Typography variant="body2" color="text.secondary">
              {completedCount} of {steps.length} sections complete
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Next Steps */}
      {nudges.length > 0 && (
        <Box sx={{ mb: 4.5 }}>
          <Typography variant="caption" sx={{ mb: 1.5, display: 'block' }}>
            Next Steps
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {nudges.slice(0, 3).map((n) => (
              <Card key={n.key} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardActionArea
                  onClick={() => onNavigate(n.key)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.75, justifyContent: 'flex-start' }}
                >
                  <Typography sx={{ fontSize: 20, width: 28, textAlign: 'center' }}>{n.icon}</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, flex: 1 }}>{n.text}</Typography>
                  <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Progress Grid */}
      <Box>
        <Typography variant="caption" sx={{ mb: 1.5, display: 'block' }}>
          Progress
        </Typography>
        <Grid container spacing={1.25}>
          {steps.map((s) => {
            const complete = s.complete;
            return (
              <Grid item xs={6} sm={4} key={s.key}>
                <Card variant="outlined" sx={{ bgcolor: '#fff' }}>
                  <CardActionArea
                    onClick={() => onNavigate(s.key)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.75, justifyContent: 'flex-start' }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: complete ? 'primary.main' : 'grey.200',
                        color: complete ? '#fff' : 'text.disabled',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {complete ? <CheckIcon sx={{ fontSize: 16 }} /> : s.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.label}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                        {s.total !== null ? `${s.done} / ${s.total} ${s.unit}` : `${s.done} ${s.unit}`}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}
